// src/pages/Cart.js
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertCircle } from "lucide-react";
import { Suspense, lazy, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import removeSound from "../assets/Remove-Item-From-Cart.wav";
import CartShippingAddress from "../components/CartShippingAddress";
import CartSignInUp from "../components/CartSignInUp";
import DeliveryOptions from "../components/uber/DeliveryOptions";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";
import axios from "../utils/axiosHandler";

// Lazy-load the heavy CheckoutForm component.
const CheckoutForm = lazy(() => import("../components/CheckoutForm"));

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateCartItem } = useCart();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [expandedDetails, setExpandedDetails] = useState(new Set());
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { user, refreshUser } = useAuth();

  // Booking package state
  const [bookingPackage, setBookingPackage] = useState(null);

  //popup
  const [showOrderSuccessPopup, setShowOrderSuccessPopup] = useState(false);
  const [orderSuccessContent, setOrderSuccessContent] = useState('');

  // Stripe state - updated
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null); // Added for error handling

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "Canada",
  });
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  // const deliveryCost = 0;

  //config const
  const { config } = useConfig();
  const onlyPricingTier = config?.onlyPricingTier || false;
  const businessOrderCount = config?.businessOrderCount || 50;
  const businessWholesalePricing = config?.businessWholesalePricing || false;
  const selectClassName = `block w-48 rounded-md border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-30 focus:border-${config.colors.primary.replace(
    "#",
    ""
  )} focus:ring-${config.colors.primary.replace("#", "")}`;

  // Delivery method state
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState("pickup");
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState(null);
  const [isRegularPriceOnly, setIsRegularPriceOnly] = useState(true);

  // Fetch the Stripe public key from the backend on mount
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        setIsStripeLoading(true);
        const { data } = await axios.get("/api/config/stripe");
        console.log("Stripe key response:", data);

        if (data.publicKey) {
          // Create the promise with the public key
          const promise = await loadStripe(data.publicKey);
          setStripePromise(promise);
        } else {
          throw new Error("No public key in response");
        }
      } catch (err) {
        console.error("Error fetching Stripe public key:", err);
        setStripeError(
          "Could not load payment system. Please try again later."
        );
      } finally {
        setIsStripeLoading(false);
      }
    };

    fetchStripeKey();
  }, []);

  useEffect(() => {
    // Set default address when user data loads or changes
    if (user && user.addresses && user.addresses.length > 0) {
      // Find the default address if it exists
      const defaultAddress = user.addresses.find((addr) => addr.isDefault);

      // If there's a default address, select it
      if (defaultAddress) {
        setSelectedAddress(
          defaultAddress._id
            ? defaultAddress._id.toString()
            : `index-${user.addresses.indexOf(defaultAddress)}`
        );
      }
      // Otherwise select the first address
      else if (!selectedAddress) {
        setSelectedAddress(
          user.addresses[0]._id ? user.addresses[0]._id.toString() : "index-0"
        );
      }
    }
  }, [user]);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const toggleDetails = (index) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedDetails(newExpanded);
  };

  useEffect(() => {
    const initialQuantities = cart.reduce(
      (acc, item, index) => ({
        ...acc,
        [index]: item.quantity || 1,
      }),
      {}
    );
    setQuantities(initialQuantities);

    // Auto-select all items
    setSelectedItems(new Set(cart.map((_, index) => index)));
  }, [cart]);

  // Load booking package from localStorage
  useEffect(() => {
    const loadBookingPackage = () => {
      try {
        const packageData = localStorage.getItem('selectedBookingPackage');
        if (packageData) {
          setBookingPackage(JSON.parse(packageData));
        } else {
          setBookingPackage(null);
        }
      } catch (error) {
        console.error("Error loading booking package:", error);
        setBookingPackage(null);
      }
    };

    // Load on mount
    loadBookingPackage();

    // Listen for updates
    const handlePackageUpdate = () => {
      loadBookingPackage();
    };

    window.addEventListener('bookingPackageUpdated', handlePackageUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('bookingPackageUpdated', handlePackageUpdate);
    };
  }, []);

  const removeBookingPackage = () => {
    try {
      localStorage.removeItem('selectedBookingPackage');
      setBookingPackage(null);
      window.dispatchEvent(new Event('bookingPackageUpdated'));
    } catch (error) {
      console.error("Error removing booking package:", error);
    }
  };

  const toggleItem = (index) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === cart.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cart.map((_, index) => index)));
    }
  };

  const generateTierOptions = (item, index) => {
    const options = [];

    // Booking items don't have pricing tiers
    if (item.itemType === "booking") {
      return options;
    }

    if (item.product.pricingTiers && item.product.pricingTiers.length > 0) {
      // Sort tiers by minQuantity
      const sortedTiers = [...item.product.pricingTiers].sort(
        (a, b) => a.minQuantity - b.minQuantity
      );

      // Add each tier as an option
      sortedTiers.forEach((tier, i) => {
        const value = tier.maxQuantity || tier.minQuantity;

        const label = tier.maxQuantity
          ? `${tier.maxQuantity} (${tier.price}/each)`
          : `${tier.minQuantity}+ (${tier.price}/each)`;

        options.push(
          <option key={i} value={value}>
            {label}
          </option>
        );
      });

      // Add the special quote request option
      options.push(
        <option key="special" value="20000">
          +20000 (Quote Request)
        </option>
      );
    } else {
      // Default options if no pricing tiers
      for (let i = 1; i <= 10; i++) {
        options.push(
          <option key={i} value={i}>
            {i}
          </option>
        );
      }
      options.push(
        <option key="special" value="20000">
          +20000 (Quote Request)
        </option>
      );
    }

    return options;
  };

  const updateQuantity = async (index, newQuantity) => {
    const item = cart[index];

    // Booking items don't have quantity restrictions
    if (item.itemType === "booking") {
      setQuantities({ ...quantities, [index]: newQuantity });
      await updateCartItem(index, { quantity: newQuantity });
      return;
    }

    const minOrder = item.product.minimumOrder || 1;

    // Special case for quote request
    if (newQuantity === 20000) {
      setQuantities({ ...quantities, [index]: newQuantity });
      await updateCartItem(index, { quantity: newQuantity });
      setCouponError(null);
      return;
    }

    // Check minimum order quantity
    if (newQuantity < minOrder) {
      setCouponError(
        `Minimum order quantity for ${item.product.name} is ${minOrder}`
      );
      return;
    }

    // Check stock status
    if (!item.product.inStock) {
      setCouponError(`${item.product.name} is currently out of stock`);
      return;
    }

    if (newQuantity > 0) {
      setQuantities({ ...quantities, [index]: newQuantity });
      await updateCartItem(index, { quantity: newQuantity });
      setCouponError(null);
    }
  };

  // Check if all items have regular prices (no wholesale/business pricing)
  // Modify this useEffect in your Cart.js
  useEffect(() => {
    if (cart.length > 0 && selectedItems.size > 0) {
      // Default to true - assume all items are regular pricing
      let hasOnlyRegularPricing = true;

      // Check each selected item
      Array.from(selectedItems).forEach((index) => {
        if (index < cart.length) {
          // Make sure index is valid
          const item = cart[index];
          // Skip pricing checks for booking items
          if (item.itemType !== "booking") {
            // Check for business/wholesale pricing or tiered pricing
            if (
              (item.product.basePrice &&
                item.product.basePrice !== item.product.regularPrice) ||
              (item.product.pricingTiers && item.product.pricingTiers.length > 0)
            ) {
              hasOnlyRegularPricing = false;
            }
          }
        }
      });

      console.log("Regular pricing only:", hasOnlyRegularPricing);
      setIsRegularPriceOnly(hasOnlyRegularPricing);
    } else {
      // If cart is empty or no items selected, default to true
      setIsRegularPriceOnly(true);
    }
  }, [cart, selectedItems]);

  // Coupon validation handler
  const validateCoupon = async () => {
    if (!couponCode) {
      setCouponError("Please enter a coupon code");
      return;
    }

    try {
      // Calculate total before tax for coupon validation using the same method as main cart
      const orderTotal = Array.from(selectedItems).reduce((sum, index) => {
        const item = cart[index];
        const qty = quantities[index];

        // For quote request items (qty >= 20000), return zero cost
        if (qty >= 20000) {
          return sum;
        }

        // Use the calculateItemPrice function to get the correct pricing
        const pricing = calculateItemPrice(item, index);
        const subtotal = parseFloat(
          typeof pricing === "string" ? pricing : pricing.total
        );

        return sum + subtotal;
      }, 0);

      const response = await axios.post(
        "/api/coupons/validate",
        { couponCode, orderTotal },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Reset any previous errors
      setCouponError(null);
      // Store the applied coupon details
      setAppliedCoupon({
        code: couponCode,
        discountAmount: response.data.discountAmount,
        details: response.data.couponDetails,
      });
    } catch (error) {
      setCouponError(
        error.response?.data?.error || "Failed to validate coupon"
      );
      setAppliedCoupon(null);
    }
  };

  useEffect(() => {
    const handleOrderSuccess = () => {
      if (config.showOrderSuccessPopup && config.orderSuccessPopupContent) {
        setOrderSuccessContent(config.orderSuccessPopupContent);
        setShowOrderSuccessPopup(true);
      } else {
        navigate('/orders');
      }
    };

    // Listen for custom order success event
    window.addEventListener('orderSuccess', handleOrderSuccess);

    return () => {
      window.removeEventListener('orderSuccess', handleOrderSuccess);
    };
  }, [config.showOrderSuccessPopup, config.orderSuccessPopupContent, navigate]);


  // Remove applied coupon
  const removeCoupon = () => {
    setCouponCode("");
    setAppliedCoupon(null);
    setCouponError(null);
  };

  const calculateItemPrice = (item, index) => {
    const qty = quantities[index];

    if (qty >= 20000) {
      return "0.00"; // Zero price for quote request
    }

    // Handle booking items
    if (item.itemType === "booking") {
      const pricePerLesson = item.bookingDetails?.pricePerLesson || 0;
      return {
        total: (pricePerLesson * qty).toFixed(2),
        unitPrice: pricePerLesson.toFixed(2),
        quantity: qty,
        regularPrice: pricePerLesson.toFixed(2),
        isBooking: true,
      };
    }

    // Get base prices for product items
    const regularPrice = item.product.regularPrice || item.product.basePrice;
    const basePrice = item.product.basePrice || regularPrice;

    // Default unit price is regular price
    let unitPrice = regularPrice;
    let pricingTier = null;
    let isBusinessOrder = false;

    // Check for wholesale pricing for business users
    if (
      config.businessWholesalePricing &&
      user &&
      user.userType === "business" &&
      item.product.basePrice
    ) {
      // Use wholesale price for business users if available
      unitPrice = item.product.basePrice;
      return {
        total: (unitPrice * qty).toFixed(2),
        unitPrice: unitPrice.toFixed(2),
        quantity: qty,
        regularPrice: regularPrice.toFixed(2),
        isWholesale: true,
        isBusinessOrder: true,
      };
    }

    // Check if the item has pricing tiers
    if (item.product.pricingTiers?.length > 0) {
      const sortedTiers = [...item.product.pricingTiers].sort(
        (a, b) => a.minQuantity - a.minQuantity
      );

      // Find applicable tier for the current quantity
      pricingTier = sortedTiers
        .filter(
          (tier) =>
            qty >= tier.minQuantity &&
            (!tier.maxQuantity || qty <= tier.maxQuantity)
        )
        .pop(); // Get the highest matching tier

      if (pricingTier) {
        // Quantity falls within a defined tier
        unitPrice = pricingTier.price;
      } else if (
        qty > sortedTiers[sortedTiers.length - 1]?.minQuantity ||
        qty >= businessOrderCount
      ) {
        // Quantity exceeds highest tier or business threshold
        if (basePrice) {
          unitPrice = basePrice; // Use base price for business orders if available
        } else {
          // Fall back to last tier price if no base price is specified
          unitPrice =
            sortedTiers[sortedTiers.length - 1]?.price || regularPrice;
        }
        isBusinessOrder = true;
      } else {
        // Quantity is lower than any tier, use regular price
        unitPrice = regularPrice;
      }
    } else {
      // Item doesn't have pricing tiers
      // Check if it qualifies for business pricing
      if (qty >= businessOrderCount) {
        unitPrice = basePrice; // Use base price for business orders
        isBusinessOrder = true;
      } else {
        unitPrice = regularPrice; // Otherwise use regular price
      }
    }

    const finalPrice = unitPrice * qty;

    // Return a detailed object with pricing info
    return {
      total: finalPrice.toFixed(2),
      unitPrice: unitPrice.toFixed(2),
      quantity: qty,
      regularPrice: regularPrice.toFixed(2),
      tier: pricingTier,
      isBusinessOrder: isBusinessOrder,
    };
  };


  // Tax calculation function with coupon integration
  // Filter out invalid cart indices before calculations
  const calculateTaxAndTotal = () => {
    const validSelectedItems = Array.from(selectedItems).filter(
      (index) => index < cart.length
    );

    const itemTotals = validSelectedItems.map((index) => {
      const item = cart[index];
      const qty = quantities[index];

      // For quote request items (qty >= 20000), return zero cost
      if (qty >= 20000) {
        return {
          subtotal: 0,
          hasGST: false,
          hasPST: false,
          isQuoteRequest: true,
        };
      }

      // Use the calculateItemPrice function to get the correct pricing
      const pricing = calculateItemPrice(item, index);
      const subtotal = parseFloat(
        typeof pricing === "string" ? pricing : pricing.total
      );

      return {
        subtotal,
        hasGST: item.itemType === "booking" ? item.bookingService?.hasGST : item.product.hasGST,
        hasPST: item.itemType === "booking" ? item.bookingService?.hasPST : item.product.hasPST,
      };
    });

    // Check if any items are quote requests
    const hasQuoteRequests = itemTotals.some((item) => item.isQuoteRequest);

    const subtotal = itemTotals.reduce((sum, item) => sum + item.subtotal, 0);
    const gstTotal = itemTotals.reduce(
      (sum, item) => (item.hasGST ? sum + item.subtotal * 0.05 : sum),
      0
    );
    const pstTotal = itemTotals.reduce(
      (sum, item) => (item.hasPST ? sum + item.subtotal * 0.07 : sum),
      0
    );

    const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
    const discountedSubtotal = subtotal - discountAmount;

    const deliveryCost = 0;

    // Check if any items have special pricing
    const hasSpecialPricing = itemTotals.some((item) => item.hasSpecialPricing);

    // Use the delivery cost from state
    const finalDeliveryCost =
      selectedDeliveryMethod === "delivery" ? deliveryCost : 0;

    return {
      subtotal,
      discountAmount,
      discountedSubtotal,
      gstTotal,
      pstTotal,
      deliveryCost: selectedDeliveryMethod === "delivery" ? deliveryCost : 0,
      total:
        discountedSubtotal +
        gstTotal +
        pstTotal +
        (selectedDeliveryMethod === "delivery" ? deliveryCost : 0),
      hasQuoteRequests,
      hasSpecialPricing,
    };
  };

  const renderPaymentMethod = () => {
    const {
      subtotal,
      discountAmount,
      discountedSubtotal,
      gstTotal,
      pstTotal,
      total,
      hasQuoteRequests,
    } = calculateTaxAndTotal();

    if (!user) {
      return (
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {gstTotal > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>GST (5%)</span>
              <span>${gstTotal.toFixed(2)}</span>
            </div>
          )}
          {pstTotal > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>PST (7%)</span>
              <span>${pstTotal.toFixed(2)}</span>
            </div>
          )}
          <div className="h-px bg-gray-200 my-2"></div>
          <div className="flex justify-between text-lg font-bold text-gray-900">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <button
            onClick={() => setShowSignInModal(true)}
            className="w-full px-6 py-3 rounded-md font-semibold mt-4 "
            style={{
              backgroundColor: `${config.colors.primary}90` || "#3B82F6",
              color: "#ffffff",
            }}
          >
            Continue
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Card Payment</h3>
          <div className="flex gap-3">
            {/* Visa Card */}
            <svg
              className="h-8"
              viewBox="0 0 780 500"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="780" height="500" fill="white" />
              <path
                d="M293.2 348.7H246.1L275.6 169.3H322.7L293.2 348.7Z"
                fill="#00579F"
              />
              <path
                d="M518.6 176.3C509.3 172.6 494.5 168.6 476.2 168.6C429.4 168.6 396.4 193.1 396.2 228C396 253.9 419.8 268.2 437.7 276.8C456.1 285.6 462.3 291.2 462.3 299.1C462.2 311.1 447.5 316.6 433.9 316.6C414.9 316.6 404.9 313.8 389.3 307L383.2 304.1L376.5 344.8C387.6 349.8 408.1 354.2 429.4 354.4C479.3 354.4 511.7 330.2 512.1 292.7C512.3 272.2 499.7 256.6 472.4 243.7C455.9 235.4 445.7 229.8 445.8 221.4C445.8 213.9 454.4 205.9 472.9 205.9C488.4 205.7 499.6 209.2 508.3 212.8L512.5 214.9L518.6 176.3Z"
                fill="#00579F"
              />
              <path
                d="M608.5 169.3H574.2C563.9 169.3 556.2 172.1 551.8 182.7L483.9 348.8H533.9C533.9 348.8 541.8 326.8 543.9 321.1C549.7 321.1 598.9 321.1 606.4 321.1C608 328.3 612.3 348.8 612.3 348.8H657.2L624.2 169.3H608.5ZM558.1 288.5C562.3 277.2 576.4 238.3 576.4 238.3C576.1 238.8 580.2 227.4 582.3 220.5L585.3 236.4C585.3 236.4 594.1 279.3 596.1 288.5H558.1Z"
                fill="#00579F"
              />
              <path
                d="M190.4 169.3L143.4 291.7L139.1 272.5C131.5 247.5 108.4 220.3 82.5 206.8L125.7 348.6H176L247.3 169.3H190.4Z"
                fill="#00579F"
              />
              <path
                d="M111.6 169.3H32.9L32 173.4C95.5 189.9 138.3 227.3 152.8 272.5L139.1 190.1C136.9 174.3 125.9 169.7 111.6 169.3Z"
                fill="#FAA61A"
              />
            </svg>

            {/* Mastercard */}
            <svg
              className="h-8"
              viewBox="0 0 780 500"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="780" height="500" fill="white" />
              <path d="M465.5 95.5h-151v260h151V95.5z" fill="#FF5F00" />
              <path
                d="M324.3 225.5c0-53 24.9-100.2 63.7-130.5-28.3-22.3-64.1-35.6-103-35.6C193 59.4 118.4 134 118.4 225.5s74.6 166.7 166.7 166.7c38.9 0 74.7-13.3 103-35.6-38.8-30.3-63.7-77.5-63.7-130.5z"
                fill="#EB001B"
              />
              <path
                d="M657.8 225.5c0 92.1-74.6 166.7-166.7 166.7-38.9 0-74.7-13.3-103-35.6 38.8-30.3 63.7-77.5 63.7-130.5s-24.9-100.2-63.7-130.5c28.3-22.3 64.1-35.6 103-35.6 92.1 0 166.7 74.6 166.7 166.7z"
                fill="#F79E1B"
              />
            </svg>
          </div>
        </div>

        {/* Display quote request message if applicable */}
        {hasQuoteRequests && (
          <Alert className="mb-4 bg-blue-50 border-blue-200">
            <div className="flex items-start">
              <AlertCircle
                className="h-5 w-5 mr-2 mt-0.5"
                style={{ color: config.colors.primary || "#3B82F6" }}
              ></AlertCircle>
              <div
                style={{
                  color: config.colors.primary || "#3B82F6",
                }}
              >
                <h4 className="font-medium ">Quote Request Included</h4>
                <AlertDescription>
                  Your cart contains items marked as Quote Request. A sales
                  representative will contact you about pricing for these items.
                </AlertDescription>
              </div>
            </div>
          </Alert>
        )}

        <div className="space-y-3 mb-6">
          {/* Coupon Section */}
          <div className="mb-4">
            <label
              htmlFor="couponCode"
              className="block text-sm font-medium text-gray-700"
            >
              Coupon Code
            </label>
            <div className="flex space-x-2 mt-1">
              <input
                type="text"
                id="couponCode"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="Enter coupon code"
                className="flex-grow border rounded px-3 py-2"
              />
              {appliedCoupon ? (
                <button
                  onClick={removeCoupon}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                >
                  Remove
                </button>
              ) : (
                <button
                  onClick={validateCoupon}
                  className="text-white px-4 py-2 rounded transition-colors"
                  style={{
                    backgroundColor: config.colors.primary || "#3B82F6",
                    ":hover": {
                      backgroundColor:
                        `${config.colors.primary}DD` || "#3B82F6",
                    },
                  }}
                >
                  Apply
                </button>
              )}
            </div>
            {couponError && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{couponError}</AlertDescription>
              </Alert>
            )}
            {appliedCoupon && (
              <div className="mt-2 text-sm text-green-600">
                Coupon "{appliedCoupon.code}" applied successfully
              </div>
            )}
          </div>

          {/* Order Summary Details */}
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>

          {/* Coupon Discount */}
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Coupon Discount</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
          )}

          {gstTotal > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>GST (5%)</span>
              <span>${gstTotal.toFixed(2)}</span>
            </div>
          )}
          {pstTotal > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>PST (7%)</span>
              <span>${pstTotal.toFixed(2)}</span>
            </div>
          )}
          <div className="h-px bg-gray-200 my-2"></div>
          <div className="flex justify-between text-lg font-bold text-gray-900">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Updated Stripe Elements rendering with proper loading and error states */}
        {isStripeLoading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading payment system...</span>
          </div>
        ) : stripeError ? (
          <div className="p-4 text-red-500 bg-red-50 rounded border border-red-200">
            {stripeError}
          </div>
        ) : stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              locale: "en",
              appearance: {
                theme: "stripe",
              },
            }}
          >
            <Suspense fallback={null}>
              <CheckoutForm
                selectedItems={selectedItems}
                quantities={quantities}
                total={total}
                coupon={appliedCoupon}
                hasQuoteRequests={hasQuoteRequests}
                selectedAddress={selectedAddress}
                deliveryInstructions={deliveryInstructions}
                selectedDeliveryMethod={selectedDeliveryMethod}
                deliveryCost={
                  selectedDeliveryMethod === "delivery" ? deliveryCost : 0
                }
                taxInfo={{
                  subtotal: subtotal,
                  discountedSubtotal: discountedSubtotal,
                  gstTotal: gstTotal,
                  pstTotal: pstTotal,
                  deliveryCost:
                    selectedDeliveryMethod === "delivery" ? deliveryCost : 0,
                }}
              />
            </Suspense>
          </Elements>
        ) : (
          <div className="p-4 text-red-500 bg-red-50 rounded border border-red-200">
            Unable to load payment system. Please refresh the page or try again
            later.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gray-50 ">
      <h2 className="text-3xl font-bold text-gray-900 mb-2 border-b pb-4">
        Shopping Cart
      </h2>

      <CartSignInUp
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
      />

      {cart.length === 0 && !bookingPackage ? (
        <p className="text-gray-600 rounded">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 320">
            {/* Background with lighter gradient */}
            <defs>
              <linearGradient
                id="backgroundGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
                <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
              </linearGradient>
              <filter
                id="lightShadow"
                x="-20%"
                y="-20%"
                width="140%"
                height="140%"
              >
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="1.5"
                  floodOpacity="0.07"
                />
              </filter>
            </defs>

            {/* Main background - transparent to make it lighter */}
            <rect
              width="500"
              height="320"
              fill="url(#backgroundGradient)"
              rx="0"
              ry="0"
              opacity="0.8"
            />

            {/* Shopping Cart Icon - lighter style - centered and smaller */}
            <g
              transform="translate(250, 120) scale(6)"
              filter="url(#lightShadow)"
            >
              {/* This is based on W3C's icon style but with lighter stroke */}
              <path
                d="M-7,8 L-9,2 L5,2 L3,8 Z"
                fill="white"
                stroke="#9ca3af"
                strokeWidth="0.45"
              />
              <line
                x1="-9"
                y1="2"
                x2="-11"
                y2="-3"
                stroke="#9ca3af"
                strokeWidth="0.45"
              />
              <line
                x1="-11"
                y1="-3"
                x2="-13"
                y2="-3"
                stroke="#9ca3af"
                strokeWidth="0.45"
              />

              {/* Cart wheels - lighter */}
              <circle
                cx="-5"
                cy="10"
                r="1.5"
                fill="white"
                stroke="#9ca3af"
                strokeWidth="0.35"
              />
              <circle
                cx="1"
                cy="10"
                r="1.5"
                fill="white"
                stroke="#9ca3af"
                strokeWidth="0.35"
              />
            </g>

            {/* Text Element - lighter color */}
            <text
              x="250"
              y="240"
              fontFamily="Arial, sans-serif"
              fontSize="18"
              fontWeight="400"
              fill="#9ca3af"
              textAnchor="middle"
            >
              Your cart is empty
            </text>
          </svg>
        </p>
      ) : (
        <>
          {cart.length > 0 && (
            <div className="mb-1 flex items-center">
              <button
                onClick={toggleSelectAll}
                style={{
                  boxShadow: "none",
                  border: "none",
                  background: "transparent",
                  transform: "none",
                }}
              >
                {selectedItems.size === cart.length
                  ? "Deselect All Items"
                  : "Select All Items"}
              </button>
            </div>
          )}

          <div className="space-y-4 mb-8">
            {/* Booking Package Display */}
            {bookingPackage && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border-2 border-blue-200 overflow-hidden mb-4 transition-all hover:shadow-md relative">
                <div className="flex flex-col sm:flex-row">
                  {/* Icon/Image column */}
                  <div className="p-4 w-48 flex sm:flex-col items-center justify-center relative bg-white/50">
                    <div className="w-full h-40 flex items-center justify-center">
                      <svg
                        className="w-24 h-24 text-blue-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Package details column */}
                  <div className="flex-1 p-4 border-t sm:border-t-0 sm:border-l border-blue-100">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                            Booking Package
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {bookingPackage.class} - {bookingPackage.name}
                        </h3>
                        {/* <p className="text-sm text-gray-600 mb-2">
                          {bookingPackage.description}
                        </p> */}

                        <div className="mt-3 space-y-1 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{bookingPackage.lessons} lesson{bookingPackage.lessons > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{bookingPackage.duration} minutes per lesson</span>
                          </div>
                          {/* Show all bookings if multiple, or single booking date */}
                          {(bookingPackage.allBookings && bookingPackage.allBookings.length > 0) ||
                            (bookingPackage.scheduledBookings && bookingPackage.scheduledBookings.length > 0) ? (
                            <div className="mt-2 space-y-1">
                              <div className="text-xs font-semibold text-gray-700 mb-1">Scheduled Lessons:</div>
                              {(bookingPackage.allBookings || bookingPackage.scheduledBookings).map((booking, idx) => (
                                <div key={idx} className="flex items-start gap-2 ml-2">
                                  <svg className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span className="text-sm text-purple-700">
                                    <span className="font-semibold">Lesson {idx + 1}:</span>{' '}
                                    {(() => {
                                      // Fix timezone issue by parsing the date string correctly
                                      const [year, month, day] = booking.date.split('-');
                                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                      return date.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      });
                                    })()} at {booking.startTime} ({booking.duration} min)
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : bookingPackage.date && bookingPackage.startTime && (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="font-semibold text-purple-700">
                                {(() => {
                                  // Fix timezone issue by parsing the date string correctly
                                  const [year, month, day] = bookingPackage.date.split('-');
                                  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                  return date.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  });
                                })()} at {bookingPackage.startTime}
                              </span>
                            </div>
                          )}
                          {bookingPackage.savings > 0 && (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-green-600 font-semibold">Save ${bookingPackage.savings}</span>
                            </div>
                          )}
                          {bookingPackage.needsCarRental && (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-blue-600">Car Rental for Road Test</span>
                            </div>
                          )}
                          {bookingPackage.needsRoadTestBooking && (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-blue-600">Book My Road Test</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => navigate(bookingPackage.bookingPageUrl || '/booking')}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                          >
                            Complete Booking
                          </button>

                        </div>
                      </div>

                      {/* Price and remove button */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-4 mt-4 sm:mt-0 sm:ml-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            ${bookingPackage.price}
                          </p>
                          {bookingPackage.lessons > 1 && (
                            <p className="text-xs text-gray-500 mt-1">
                              ${(bookingPackage.price / bookingPackage.lessons).toFixed(2)} per lesson
                            </p>
                          )}
                        </div>

                        <button
                          onClick={removeBookingPackage}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          aria-label="Remove booking package"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {cart.map((item, index) => {
              const isBooking = item.itemType === "booking";
              const itemData = isBooking ? item.bookingService : item.product;

              return (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-4 transition-all hover:shadow-md relative"
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Checkbox and Image column */}
                    <div className="p-4 w-48  flex sm:flex-col items-start relative">
                      <Checkbox
                        checked={selectedItems.has(index)}
                        onCheckedChange={() => toggleItem(index)}
                        className="mt-1 mr-4 sm:mr-0 sm:mb-4 h-5 w-5"
                      />
                      {/* Mobile remove button - visible ONLY on small screens */}
                      <button
                        onClick={() => {
                          // Try to play remove sound
                          try {
                            const audio = new Audio(removeSound);
                            audio
                              .play()
                              .catch((e) =>
                                console.log("Audio failed to play:", e)
                              );
                          } catch (e) {
                            console.log("Audio error:", e);
                          }
                          removeFromCart(index);
                        }}
                        className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm z-10 text-red-500 sm:hidden"
                        aria-label="Remove item"
                        style={{
                          boxShadow: "none",
                          border: "none",
                          background: "transparent",
                          transform: "none",
                          marginLeft: "auto",
                          marginRight: -120 + "px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>

                      {/* Larger image */}
                      <div className="w-full sm:w-full h-40 flex items-center justify-center flex-shrink-0">
                        {isBooking ? (
                          // Booking service icon
                          <div className="w-full h-full flex items-center justify-center bg-blue-50 rounded-md">
                            <svg
                              className="w-20 h-20 text-blue-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        ) : (
                          <img
                            src={
                              // First priority: preview URL from processed customization
                              item.customization?.previewUrl ||
                              // Second priority: preview path with API prefix
                              (item.customization?.preview &&
                                item.customization.preview.startsWith("/")
                                ? `/api${item.customization.preview}`
                                : item.customization.preview) ||
                              // Third priority: product images with new file structure
                              (item.product?.images?.[0]?.path
                                ? `/api${item.product.images[0].path}`
                                : item.product?.images?.[0]?.data) ||
                              // Fourth priority: custom field images with URL handling
                              (() => {
                                const imageField =
                                  item.customization?.customFields?.find(
                                    (field) =>
                                      (field.type === "image" ||
                                        field.type === "logo") &&
                                      (field.imageUrl || field.content)
                                  );
                                if (imageField) {
                                  if (
                                    imageField.imageUrl &&
                                    imageField.imageUrl.startsWith("/")
                                  ) {
                                    return `/api${imageField.imageUrl}`;
                                  }
                                  return imageField.content;
                                }
                                return null;
                              })() ||
                              // Final fallback
                              "https://via.placeholder.com/150?text=No+Image"
                            }
                            alt={item.product?.name}
                            className="w-full h-full object-cover rounded-md"
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/150?text=No+Image";
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Product/Booking details column */}
                    <div className="flex-1 p-4 border-t sm:border-t-0 sm:border-l border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                        <div className="flex-1">
                          {isBooking && (
                            <span className="inline-block px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full mb-2">
                              Booking Service
                            </span>
                          )}
                          <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {isBooking
                              ? `${itemData?.name || 'Booking Service'} - ${item.bookingDetails?.selectedClass || ''}`
                              : item.product?.name}
                          </h3>
                          {isBooking && item.bookingDetails && (
                            <div className="text-sm text-gray-600 space-y-1 mb-3">
                              <p>Duration: {item.bookingDetails.duration} minutes</p>
                              <p>Type: {item.bookingDetails.bookingType === 'single' ? 'Single Lesson' : 'Package'}</p>
                              {item.bookingDetails.pricePerLesson && (
                                <p className="font-semibold text-blue-600">
                                  ${item.bookingDetails.pricePerLesson} per lesson
                                </p>
                              )}
                            </div>
                          )}

                          {/* Quantity selector - hide for bookings */}
                          {!isBooking && (
                            <div className="mt-4 flex items-center">
                              <label
                                htmlFor={`quantity-${index}`}
                                className="text-sm font-medium text-gray-700 mr-3"
                              >
                                Quantity:
                              </label>

                              {onlyPricingTier ? (
                                // When onlyPricingTier is true, use different UI based on whether item has pricing tiers
                                !isBooking && item.product.pricingTiers &&
                                  item.product.pricingTiers.length > 0 ? (
                                  // Use dropdown for items with pricing tiers when onlyPricingTier is true
                                  <select
                                    id={`quantity-${index}`}
                                    value={quantities[index]}
                                    onChange={(e) =>
                                      updateQuantity(
                                        index,
                                        parseInt(e.target.value)
                                      )
                                    }
                                    className={selectClassName}
                                    style={{
                                      borderColor: config.colors.textSecondary,
                                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                      ":focus": {
                                        borderColor: config.colors.primary,
                                        boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                                        outline: "none",
                                      },
                                    }}
                                  >
                                    {generateTierOptions(item, index)}
                                  </select>
                                ) : (
                                  // Use +/- increment for items without pricing tiers when onlyPricingTier is true
                                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                                    <button
                                      onClick={() =>
                                        updateQuantity(index, quantities[index] - 1)
                                      }
                                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
                                      disabled={
                                        !isBooking && quantities[index] <=
                                        (item.product?.minimumOrder || 1)
                                      }
                                    >
                                      −
                                    </button>
                                    <input
                                      type="number"
                                      value={quantities[index]}
                                      onChange={(e) =>
                                        updateQuantity(
                                          index,
                                          parseInt(e.target.value)
                                        )
                                      }
                                      className="w-16 text-center border-x border-gray-300 py-1 focus:outline-none"
                                      min={!isBooking ? (item.product?.minimumOrder || 1) : 1}
                                    />
                                    <button
                                      onClick={() =>
                                        updateQuantity(index, quantities[index] + 1)
                                      }
                                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
                                    >
                                      +
                                    </button>
                                  </div>
                                )
                              ) : (
                                // When onlyPricingTier is false, always use +/- increment regardless of pricing tiers
                                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                                  <button
                                    onClick={() =>
                                      updateQuantity(index, quantities[index] - 1)
                                    }
                                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
                                    disabled={
                                      !isBooking && quantities[index] <=
                                      (item.product?.minimumOrder || 1)
                                    }
                                  >
                                    −
                                  </button>
                                  <input
                                    type="number"
                                    value={quantities[index]}
                                    onChange={(e) =>
                                      updateQuantity(
                                        index,
                                        parseInt(e.target.value)
                                      )
                                    }
                                    className="w-24 text-center border-x border-gray-300 py-1 focus:outline-none"
                                    min={!isBooking ? (item.product?.minimumOrder || 1) : 1}
                                  />
                                  <button
                                    onClick={() =>
                                      updateQuantity(index, quantities[index] + 1)
                                    }
                                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
                                  >
                                    +
                                  </button>
                                </div>
                              )}

                              {/* Add Quote Request button */}
                              {/* {!onlyPricingTier && (
                        <button
                          onClick={() => updateQuantity(index, 20000)}
                          className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 focus:outline-none"
                        >
                          Request Quote
                        </button>
                      )} */}
                            </div>
                          )}

                          {/* Order Description - if exists */}
                          {item.customization?.description && (
                            <div className="text-sm text-gray-600 mb-3 max-w-md mt-6">
                              <span className="font-medium text-gray-700">
                                Description:{" "}
                              </span>
                              {item.customization.description}
                            </div>
                          )}

                          {/* Customization Details Toggle */}
                          {(item.customization?.template ||
                            (item.customization?.requiredFields &&
                              item.customization?.requiredFields.length > 0) ||
                            (item.customization?.customFields &&
                              item.customization?.customFields.length > 0)) && (
                              <button
                                onClick={() => toggleDetails(index)}
                                style={{
                                  boxShadow: "none",
                                  border: "none",
                                  background: "transparent",
                                  transform: "none",
                                  color: config.colors.primary,
                                }}
                                className=" text-sm font-medium flex items-center mt-4"
                              >
                                {expandedDetails.has(index) ? (
                                  <>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4 mr-1"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                    Hide Details
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4 mr-1"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                    Show Details
                                  </>
                                )}
                              </button>
                            )}

                          {/* Expanded Customization Details */}
                          {expandedDetails.has(index) && (
                            <div className="mt-4 space-y-3 p-3 bg-gray-50 rounded-md max-w-md">
                              {item.customization?.template && (
                                <div className="text-sm">
                                  <span className="font-medium text-gray-700">
                                    Template:{" "}
                                  </span>
                                  <span className="text-gray-600">
                                    {item.customization.template.name}
                                  </span>
                                </div>
                              )}

                              {/* Required Fields */}
                              {item.customization?.requiredFields?.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 mb-1">
                                    Required Fields:
                                  </h4>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {item.customization.requiredFields.map(
                                      (field, fieldIndex) => (
                                        <div key={fieldIndex} className="text-sm">
                                          <p className="font-medium text-gray-700">
                                            {field.type}:
                                          </p>
                                          {field.type === "text" ? (
                                            <p className="text-gray-600">
                                              {field.value}
                                            </p>
                                          ) : (
                                            <img
                                              src={field.value}
                                              alt={`${field.type}`}
                                              className="w-16 h-16 object-contain border rounded"
                                            />
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Custom Fields */}
                              {item.customization?.customFields?.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 mb-1">
                                    Custom Fields:
                                  </h4>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {item.customization.customFields.map(
                                      (field, fieldIndex) => (
                                        <div key={fieldIndex} className="text-sm">
                                          <p className="font-medium text-gray-700">
                                            {field.type}:
                                          </p>
                                          {field.type === "text" ? (
                                            <p className="text-gray-600">
                                              {field.content}
                                            </p>
                                          ) : (
                                            // <img
                                            //   src={field.content}
                                            //   alt="Custom upload"
                                            //   className="w-106 h-106 object-contain border rounded"
                                            // />
                                            <p></p>
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="mt-4 sm:mt-0 sm:ml-6 sm:text-right">
                          {/* Desktop remove button - ONLY visible on larger screens */}
                          <button
                            onClick={() => {
                              // Try to play remove sound
                              try {
                                const audio = new Audio(removeSound);
                                audio
                                  .play()
                                  .catch((e) =>
                                    console.log("Audio failed to play:", e)
                                  );
                              } catch (e) {
                                console.log("Audio error:", e);
                              }
                              removeFromCart(index);
                            }}
                            style={{
                              boxShadow: "none",
                              border: "none",
                              background: "transparent",
                              transform: "none",
                              marginLeft: 100 + "px",
                            }}
                            className="text-red-500 hover:text-red-700 hidden sm:flex items-right text-sm font-medium mb-2"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            {/* Remove */}
                          </button>

                          {/* Price information */}
                          {quantities[index] < 20000 ? (
                            <div className="mt-2">
                              {typeof calculateItemPrice(item, index) ===
                                "string" ? (
                                // Legacy string format handling
                                <div className="text-xl font-semibold text-gray-900">
                                  ${calculateItemPrice(item, index)}
                                </div>
                              ) : (
                                // New detailed price object format
                                <div>
                                  <div className="text-xl font-semibold text-gray-900">
                                    ${calculateItemPrice(item, index).total}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {/* Show unit price and quantity */}
                                    <div>
                                      {calculateItemPrice(item, index).quantity} ×
                                      ${calculateItemPrice(item, index).unitPrice}
                                      /each
                                    </div>

                                    {/* Show pricing information based on scenario */}
                                    {/* {!onlyPricingTier && (
                                  <div>
                                    {calculateItemPrice(item, index).tier ? (
                                      // Show tier information
                                      <div style={{ color: config.colors.secondary }}>
                                        {calculateItemPrice(item, index).tier.maxQuantity 
                                          ? `Tier: ${calculateItemPrice(item, index).tier.minQuantity}-${calculateItemPrice(item, index).tier.maxQuantity}` 
                                          : `Tier: ${calculateItemPrice(item, index).tier.minQuantity}+`}
                                      </div>
                                    ) : calculateItemPrice(item, index).isBusinessOrder ? (
                                      // Show business order indication
                                      <div style={{ color: config.colors.primary, fontWeight: 500 }}>Business Pricing</div>
                                    ) : (
                                      // Show regular price indication
                                      <div>Regular Price</div>
                                    )}
                                  </div>
                                )} */}
                                    {!onlyPricingTier && (
                                      <div>
                                        {calculateItemPrice(item, index)
                                          .isWholesale ? (
                                          // Show wholesale pricing indication
                                          <div
                                            style={{
                                              color: config.colors.primary,
                                              fontWeight: 500,
                                            }}
                                          >
                                            Wholesale Pricing
                                          </div>
                                        ) : calculateItemPrice(item, index)
                                          .tier ? (
                                          // Show tier information
                                          <div
                                            style={{
                                              color: config.colors.secondary,
                                            }}
                                          >
                                            {calculateItemPrice(item, index).tier
                                              .maxQuantity
                                              ? `Tier: ${calculateItemPrice(item, index)
                                                .tier.minQuantity
                                              }-${calculateItemPrice(item, index)
                                                .tier.maxQuantity
                                              }`
                                              : `Tier: ${calculateItemPrice(item, index)
                                                .tier.minQuantity
                                              }+`}
                                          </div>
                                        ) : calculateItemPrice(item, index)
                                          .isBusinessOrder ? (
                                          // Show business order indication
                                          <div
                                            style={{
                                              color: config.colors.primary,
                                              fontWeight: 500,
                                            }}
                                          >
                                            Business Pricing
                                          </div>
                                        ) : (
                                          // Show regular price indication
                                          <div>Regular Price</div>
                                        )}
                                      </div>
                                    )}

                                    {/* Tax information */}
                                    {item.product?.hasGST && (
                                      <div>+ GST (5%)</div>
                                    )}
                                    {item.product?.hasPST && (
                                      <div>+ PST (7%)</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                              style={{
                                backgroundColor: `${config.colors.primary}20`,
                                color: config.colors.primary,
                              }}
                            >
                              Quote Request
                            </div>
                          )}

                          {!isBooking && !item.product?.inStock && (
                            <div className="mt-2 text-red-600 font-medium text-sm">
                              Out of stock
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* {selectedItems.size > 0 && user && renderAddressSection()} */}
          {selectedItems.size > 0 && user && (
            <CartShippingAddress
              user={user}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              deliveryInstructions={deliveryInstructions}
              setDeliveryInstructions={setDeliveryInstructions}
              refreshUser={refreshUser}
            />
          )}

          {selectedItems.size > 0 && user &&
            isRegularPriceOnly && config.uberDeliveryForItemsWithRegularPrice &&
            (
              <DeliveryOptions
                selectedDeliveryMethod={selectedDeliveryMethod}
                setSelectedDeliveryMethod={setSelectedDeliveryMethod}
                deliveryCost={deliveryCost}
                setDeliveryCost={setDeliveryCost}
                userLocation={user?.addresses?.find(
                  (addr) =>
                    addr._id?.toString() === selectedAddress ||
                    `index-${user.addresses.indexOf(addr)}` === selectedAddress
                )}
                estimatedDeliveryTime={estimatedDeliveryTime}
                setEstimatedDeliveryTime={setEstimatedDeliveryTime}
              />
            )}

          {selectedItems.size > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">
                  Order Summary
                </h3>
                <div className="h-px bg-gray-200 my-4"></div>
                {renderPaymentMethod()}

                {/* Delivery Fee if applicable */}
                {selectedDeliveryMethod === "delivery" && deliveryCost > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Delivery Fee</span>
                    <span>${deliveryCost.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Success Popup - add this before the closing </div> of the main container
      // {showOrderSuccessPopup && (
      //   <div
      //     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      //     style={{ zIndex: 9999 }}
      //   >
      //     <div className="bg-white rounded-lg max-w-lg w-full mx-4 p-6 max-h-96 overflow-y-auto">
      //       <div className="mb-6">
      //         <h2 className="text-xl font-semibold mb-4 text-gray-900">Order Confirmation</h2>
      //         <div
      //           className="text-gray-700 leading-relaxed"
      //           dangerouslySetInnerHTML={{ __html: orderSuccessContent }}
      //         />
      //       </div>
      //       <div className="flex justify-center">
      //         <button
      //           onClick={() => {
      //             setShowOrderSuccessPopup(false);
      //             navigate('/orders');
      //           }}
      //           className="w-full text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-600 transition-colors"
      //           style={{ backgroundColor: config.colors.primary }}
      //         >
      //           Continue to Orders
      //         </button>
      //       </div>
      //     </div>
      //   </div>
      // )} */}
      {/* Order Success Popup - add this before the closing </div> of the main container */}
      {/* Order Success Popup - add this before the closing </div> of the main container */}
      {showOrderSuccessPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => {
            setShowOrderSuccessPopup(false);
            navigate('/orders');
          }}
        >
          <div
            className="bg-white rounded-lg max-w-lg lg:max-w-2xl xl:max-w-4xl w-full mx-4 p-6 max-h-96 lg:max-h-[32rem] xl:max-h-[36rem] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowOrderSuccessPopup(false);
                navigate('/orders');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="mb-6 pr-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Order Confirmation</h2>
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: orderSuccessContent }}
              />
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowOrderSuccessPopup(false);
                  navigate('/orders');
                }}
                className="w-full text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-600 transition-colors"
                style={{ backgroundColor: config.colors.primary }}
              >
                Continue to Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;







// // before popup
// // src/pages/Cart.js
// import { Elements } from "@stripe/react-stripe-js";
// import { loadStripe } from "@stripe/stripe-js";
// import { AlertCircle } from "lucide-react";
// import React, { Suspense, lazy, useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import removeSound from "../assets/Remove-Item-From-Cart.wav";
// import CartShippingAddress from "../components/CartShippingAddress";
// import CartSignInUp from "../components/CartSignInUp";
// import DeliveryOptions from "../components/uber/DeliveryOptions";
// import { Alert, AlertDescription } from "../components/ui/alert";
// import { Checkbox } from "../components/ui/checkbox";
// import { useAuth } from "../context/AuthContext";
// import { useCart } from "../context/CartContext";
// import { useConfig } from "../context/ConfigContext";

// // Lazy-load the heavy CheckoutForm component.
// const CheckoutForm = lazy(() => import("../components/CheckoutForm"));

// const Cart = () => {
//   const navigate = useNavigate();
//   const { cart, removeFromCart, updateCartItem } = useCart();
//   const [selectedItems, setSelectedItems] = useState(new Set());
//   const [quantities, setQuantities] = useState({});
//   const [expandedDetails, setExpandedDetails] = useState(new Set());
//   const [showSignInModal, setShowSignInModal] = useState(false);
//   const { user, refreshUser } = useAuth();

//   // Stripe state - updated
//   const [stripePromise, setStripePromise] = useState(null);
//   const [isStripeLoading, setIsStripeLoading] = useState(true);
//   const [stripeError, setStripeError] = useState(null); // Added for error handling

//   const [selectedAddress, setSelectedAddress] = useState(null);
//   const [deliveryInstructions, setDeliveryInstructions] = useState("");
//   const [showAddressDialog, setShowAddressDialog] = useState(false);
//   const [newAddress, setNewAddress] = useState({
//     street: "",
//     city: "",
//     state: "",
//     postalCode: "",
//     country: "Canada",
//   });
//   const [isShippingExpanded, setIsShippingExpanded] = useState(false);
//   // const deliveryCost = 0;

//   //config const
//   const { config } = useConfig();
//   const onlyPricingTier = config?.onlyPricingTier || false;
//   const businessOrderCount = config?.businessOrderCount || 50;
//   const businessWholesalePricing = config?.businessWholesalePricing || false;
//   const selectClassName = `block w-48 rounded-md border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-30 focus:border-${config.colors.primary.replace(
//     "#",
//     ""
//   )} focus:ring-${config.colors.primary.replace("#", "")}`;

//   // Delivery method state
//   const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
//     useState("pickup");
//   const [deliveryCost, setDeliveryCost] = useState(0);
//   const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState(null);
//   const [isRegularPriceOnly, setIsRegularPriceOnly] = useState(true);

//   // Fetch the Stripe public key from the backend on mount
//   useEffect(() => {
//     const fetchStripeKey = async () => {
//       try {
//         setIsStripeLoading(true);
//         const { data } = await axios.get("/api/config/stripe");
//         console.log("Stripe key response:", data);

//         if (data.publicKey) {
//           // Create the promise with the public key
//           const promise = await loadStripe(data.publicKey);
//           setStripePromise(promise);
//         } else {
//           throw new Error("No public key in response");
//         }
//       } catch (err) {
//         console.error("Error fetching Stripe public key:", err);
//         setStripeError(
//           "Could not load payment system. Please try again later."
//         );
//       } finally {
//         setIsStripeLoading(false);
//       }
//     };

//     fetchStripeKey();
//   }, []);

//   useEffect(() => {
//     // Set default address when user data loads or changes
//     if (user && user.addresses && user.addresses.length > 0) {
//       // Find the default address if it exists
//       const defaultAddress = user.addresses.find((addr) => addr.isDefault);

//       // If there's a default address, select it
//       if (defaultAddress) {
//         setSelectedAddress(
//           defaultAddress._id
//             ? defaultAddress._id.toString()
//             : `index-${user.addresses.indexOf(defaultAddress)}`
//         );
//       }
//       // Otherwise select the first address
//       else if (!selectedAddress) {
//         setSelectedAddress(
//           user.addresses[0]._id ? user.addresses[0]._id.toString() : "index-0"
//         );
//       }
//     }
//   }, [user]);

//   // Coupon state
//   const [couponCode, setCouponCode] = useState("");
//   const [couponError, setCouponError] = useState(null);
//   const [appliedCoupon, setAppliedCoupon] = useState(null);

//   const toggleDetails = (index) => {
//     const newExpanded = new Set(expandedDetails);
//     if (newExpanded.has(index)) {
//       newExpanded.delete(index);
//     } else {
//       newExpanded.add(index);
//     }
//     setExpandedDetails(newExpanded);
//   };

//   useEffect(() => {
//     const initialQuantities = cart.reduce(
//       (acc, item, index) => ({
//         ...acc,
//         [index]: item.quantity || 1,
//       }),
//       {}
//     );
//     setQuantities(initialQuantities);

//     // Auto-select all items
//     setSelectedItems(new Set(cart.map((_, index) => index)));
//   }, [cart]);

//   const toggleItem = (index) => {
//     const newSelected = new Set(selectedItems);
//     if (newSelected.has(index)) {
//       newSelected.delete(index);
//     } else {
//       newSelected.add(index);
//     }
//     setSelectedItems(newSelected);
//   };

//   const toggleSelectAll = () => {
//     if (selectedItems.size === cart.length) {
//       setSelectedItems(new Set());
//     } else {
//       setSelectedItems(new Set(cart.map((_, index) => index)));
//     }
//   };

//   const generateTierOptions = (item, index) => {
//     const options = [];

//     if (item.product.pricingTiers && item.product.pricingTiers.length > 0) {
//       // Sort tiers by minQuantity
//       const sortedTiers = [...item.product.pricingTiers].sort(
//         (a, b) => a.minQuantity - b.minQuantity
//       );

//       // Add each tier as an option
//       sortedTiers.forEach((tier, i) => {
//         const value = tier.maxQuantity || tier.minQuantity;

//         const label = tier.maxQuantity
//           ? `${tier.maxQuantity} (${tier.price}/each)`
//           : `${tier.minQuantity}+ (${tier.price}/each)`;

//         options.push(
//           <option key={i} value={value}>
//             {label}
//           </option>
//         );
//       });

//       // Add the special quote request option
//       options.push(
//         <option key="special" value="20000">
//           +20000 (Quote Request)
//         </option>
//       );
//     } else {
//       // Default options if no pricing tiers
//       for (let i = 1; i <= 10; i++) {
//         options.push(
//           <option key={i} value={i}>
//             {i}
//           </option>
//         );
//       }
//       options.push(
//         <option key="special" value="20000">
//           +20000 (Quote Request)
//         </option>
//       );
//     }

//     return options;
//   };

//   const updateQuantity = async (index, newQuantity) => {
//     const item = cart[index];
//     const minOrder = item.product.minimumOrder || 1;

//     // Special case for quote request
//     if (newQuantity === 20000) {
//       setQuantities({ ...quantities, [index]: newQuantity });
//       await updateCartItem(index, { quantity: newQuantity });
//       setCouponError(null);
//       return;
//     }

//     // Check minimum order quantity
//     if (newQuantity < minOrder) {
//       setCouponError(
//         `Minimum order quantity for ${item.product.name} is ${minOrder}`
//       );
//       return;
//     }

//     // Check stock status
//     if (!item.product.inStock) {
//       setCouponError(`${item.product.name} is currently out of stock`);
//       return;
//     }

//     if (newQuantity > 0) {
//       setQuantities({ ...quantities, [index]: newQuantity });
//       await updateCartItem(index, { quantity: newQuantity });
//       setCouponError(null);
//     }
//   };

//   // Check if all items have regular prices (no wholesale/business pricing)
//   // Modify this useEffect in your Cart.js
//   useEffect(() => {
//     if (cart.length > 0 && selectedItems.size > 0) {
//       // Default to true - assume all items are regular pricing
//       let hasOnlyRegularPricing = true;

//       // Check each selected item
//       Array.from(selectedItems).forEach((index) => {
//         if (index < cart.length) {
//           // Make sure index is valid
//           const item = cart[index];
//           // Check for business/wholesale pricing or tiered pricing
//           if (
//             (item.product.basePrice &&
//               item.product.basePrice !== item.product.regularPrice) ||
//             (item.product.pricingTiers && item.product.pricingTiers.length > 0)
//           ) {
//             hasOnlyRegularPricing = false;
//           }
//         }
//       });

//       console.log("Regular pricing only:", hasOnlyRegularPricing);
//       setIsRegularPriceOnly(hasOnlyRegularPricing);
//     } else {
//       // If cart is empty or no items selected, default to true
//       setIsRegularPriceOnly(true);
//     }
//   }, [cart, selectedItems]);

//   // Coupon validation handler
//   const validateCoupon = async () => {
//     if (!couponCode) {
//       setCouponError("Please enter a coupon code");
//       return;
//     }

//     try {
//       // Calculate total before tax for coupon validation
//       const orderTotal = Array.from(selectedItems).reduce(
//         (sum, index) => sum + cart[index].product.basePrice * quantities[index],
//         0
//       );

//       const response = await axios.post(
//         "/api/coupons/validate",
//         { couponCode, orderTotal },
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`,
//           },
//         }
//       );

//       // Reset any previous errors
//       setCouponError(null);
//       // Store the applied coupon details
//       setAppliedCoupon({
//         code: couponCode,
//         discountAmount: response.data.discountAmount,
//         details: response.data.couponDetails,
//       });
//     } catch (error) {
//       setCouponError(
//         error.response?.data?.error || "Failed to validate coupon"
//       );
//       setAppliedCoupon(null);
//     }
//   };

//   // Remove applied coupon
//   const removeCoupon = () => {
//     setCouponCode("");
//     setAppliedCoupon(null);
//     setCouponError(null);
//   };

//   const calculateItemPrice = (item, index) => {
//     const qty = quantities[index];

//     if (qty >= 20000) {
//       return "0.00"; // Zero price for quote request
//     }

//     // Get base prices
//     const regularPrice = item.product.regularPrice || item.product.basePrice;
//     const basePrice = item.product.basePrice || regularPrice;

//     // Default unit price is regular price
//     let unitPrice = regularPrice;
//     let pricingTier = null;
//     let isBusinessOrder = false;

//     // Check for wholesale pricing for business users
//     if (
//       config.businessWholesalePricing &&
//       user &&
//       user.userType === "business" &&
//       item.product.basePrice
//     ) {
//       // Use wholesale price for business users if available
//       unitPrice = item.product.basePrice;
//       return {
//         total: (unitPrice * qty).toFixed(2),
//         unitPrice: unitPrice.toFixed(2),
//         quantity: qty,
//         regularPrice: regularPrice.toFixed(2),
//         isWholesale: true,
//         isBusinessOrder: true,
//       };
//     }

//     // Check if the item has pricing tiers
//     if (item.product.pricingTiers?.length > 0) {
//       const sortedTiers = [...item.product.pricingTiers].sort(
//         (a, b) => a.minQuantity - a.minQuantity
//       );

//       // Find applicable tier for the current quantity
//       pricingTier = sortedTiers
//         .filter(
//           (tier) =>
//             qty >= tier.minQuantity &&
//             (!tier.maxQuantity || qty <= tier.maxQuantity)
//         )
//         .pop(); // Get the highest matching tier

//       if (pricingTier) {
//         // Quantity falls within a defined tier
//         unitPrice = pricingTier.price;
//       } else if (
//         qty > sortedTiers[sortedTiers.length - 1]?.minQuantity ||
//         qty >= businessOrderCount
//       ) {
//         // Quantity exceeds highest tier or business threshold
//         if (basePrice) {
//           unitPrice = basePrice; // Use base price for business orders if available
//         } else {
//           // Fall back to last tier price if no base price is specified
//           unitPrice =
//             sortedTiers[sortedTiers.length - 1]?.price || regularPrice;
//         }
//         isBusinessOrder = true;
//       } else {
//         // Quantity is lower than any tier, use regular price
//         unitPrice = regularPrice;
//       }
//     } else {
//       // Item doesn't have pricing tiers
//       // Check if it qualifies for business pricing
//       if (qty >= businessOrderCount) {
//         unitPrice = basePrice; // Use base price for business orders
//         isBusinessOrder = true;
//       } else {
//         unitPrice = regularPrice; // Otherwise use regular price
//       }
//     }

//     const finalPrice = unitPrice * qty;

//     // Return a detailed object with pricing info
//     return {
//       total: finalPrice.toFixed(2),
//       unitPrice: unitPrice.toFixed(2),
//       quantity: qty,
//       regularPrice: regularPrice.toFixed(2),
//       tier: pricingTier,
//       isBusinessOrder: isBusinessOrder,
//     };
//   };

//   // const getPricingDetails = (item, quantity) => {
//   //   if (!item.product.pricingTiers || item.product.pricingTiers.length === 0) {
//   //     // No tiers, just show the unit price
//   //     const unitPrice = item.product.regularPrice || item.product.basePrice;
//   //     return <div>{quantity} × ${unitPrice.toFixed(2)}/each</div>;
//   //   }

//   //   // Sort tiers by minQuantity
//   //   const sortedTiers = [...item.product.pricingTiers].sort((a, b) => a.minQuantity - b.minQuantity);

//   //   // Find the applicable tier for the current quantity
//   //   const applicableTier = sortedTiers
//   //     .filter(tier => quantity >= tier.minQuantity && (!tier.maxQuantity || quantity <= tier.maxQuantity))
//   //     .pop(); // Get the highest matching tier

//   //   // If no tier found but quantity is higher than highest tier's minQuantity
//   //   if (!applicableTier && quantity > sortedTiers[sortedTiers.length - 1]?.minQuantity) {
//   //     // Using the last tier's price
//   //     const lastTier = sortedTiers[sortedTiers.length - 1];
//   //     return (
//   //       <div>
//   //         {quantity} × ${lastTier.price.toFixed(2)}/each
//   //         <div className="text-green-600">Special pricing applied (highest tier)</div>
//   //       </div>
//   //     );
//   //   } else if (applicableTier) {
//   //     return (
//   //       <div>
//   //         {quantity} × ${applicableTier.price.toFixed(2)}/each
//   //         <div className="text-green-600">
//   //           {applicableTier.maxQuantity
//   //             ? `Tier: ${applicableTier.minQuantity}-${applicableTier.maxQuantity}`
//   //             : `Tier: ${applicableTier.minQuantity}+`}
//   //         </div>
//   //       </div>
//   //     );
//   //   } else {
//   //     // No applicable tier, using regular price
//   //     const unitPrice = item.product.regularPrice || item.product.basePrice;
//   //     return <div>{quantity} × ${unitPrice.toFixed(2)}/each</div>;
//   //   }
//   // };

//   // Tax calculation function with coupon integration
//   // Filter out invalid cart indices before calculations
//   const calculateTaxAndTotal = () => {
//     const validSelectedItems = Array.from(selectedItems).filter(
//       (index) => index < cart.length
//     );

//     const itemTotals = validSelectedItems.map((index) => {
//       const item = cart[index];
//       const qty = quantities[index];

//       // For quote request items (qty >= 20000), return zero cost
//       if (qty >= 20000) {
//         return {
//           subtotal: 0,
//           hasGST: false,
//           hasPST: false,
//           isQuoteRequest: true,
//         };
//       }

//       // Use the calculateItemPrice function to get the correct pricing
//       const pricing = calculateItemPrice(item, index);
//       const subtotal = parseFloat(
//         typeof pricing === "string" ? pricing : pricing.total
//       );

//       return {
//         subtotal,
//         hasGST: item.product.hasGST,
//         hasPST: item.product.hasPST,
//       };
//     });

//     // Check if any items are quote requests
//     const hasQuoteRequests = itemTotals.some((item) => item.isQuoteRequest);

//     const subtotal = itemTotals.reduce((sum, item) => sum + item.subtotal, 0);
//     const gstTotal = itemTotals.reduce(
//       (sum, item) => (item.hasGST ? sum + item.subtotal * 0.05 : sum),
//       0
//     );
//     const pstTotal = itemTotals.reduce(
//       (sum, item) => (item.hasPST ? sum + item.subtotal * 0.07 : sum),
//       0
//     );

//     const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
//     const discountedSubtotal = subtotal - discountAmount;

//     const deliveryCost = 0;

//     // Check if any items have special pricing
//     const hasSpecialPricing = itemTotals.some((item) => item.hasSpecialPricing);

//     // Use the delivery cost from state
//     const finalDeliveryCost =
//       selectedDeliveryMethod === "delivery" ? deliveryCost : 0;

//     return {
//       subtotal,
//       discountAmount,
//       discountedSubtotal,
//       gstTotal,
//       pstTotal,
//       deliveryCost: selectedDeliveryMethod === "delivery" ? deliveryCost : 0,
//       total:
//         discountedSubtotal +
//         gstTotal +
//         pstTotal +
//         (selectedDeliveryMethod === "delivery" ? deliveryCost : 0),
//       hasQuoteRequests,
//       hasSpecialPricing,
//     };
//   };

//   const renderPaymentMethod = () => {
//     const {
//       subtotal,
//       discountAmount,
//       discountedSubtotal,
//       gstTotal,
//       pstTotal,
//       total,
//       hasQuoteRequests,
//     } = calculateTaxAndTotal();

//     if (!user) {
//       return (
//         <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
//           <div className="flex justify-between text-gray-600">
//             <span>Subtotal</span>
//             <span>${subtotal.toFixed(2)}</span>
//           </div>
//           {gstTotal > 0 && (
//             <div className="flex justify-between text-gray-600">
//               <span>GST (5%)</span>
//               <span>${gstTotal.toFixed(2)}</span>
//             </div>
//           )}
//           {pstTotal > 0 && (
//             <div className="flex justify-between text-gray-600">
//               <span>PST (7%)</span>
//               <span>${pstTotal.toFixed(2)}</span>
//             </div>
//           )}
//           <div className="h-px bg-gray-200 my-2"></div>
//           <div className="flex justify-between text-lg font-bold text-gray-900">
//             <span>Total</span>
//             <span>${total.toFixed(2)}</span>
//           </div>
//           <button
//             onClick={() => setShowSignInModal(true)}
//             className="w-full px-6 py-3 rounded-md font-semibold mt-4 "
//             style={{
//               backgroundColor: `${config.colors.primary}20` || "#3B82F6",
//               color: "#ffffff",
//             }}
//           >
//             Continue
//           </button>
//         </div>
//       );
//     }

//     return (
//       <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
//         <div className="flex items-center justify-between mb-6">
//           <h3 className="text-lg font-semibold text-gray-900">Card Payment</h3>
//           <div className="flex gap-3">
//             {/* Visa Card */}
//             <svg
//               className="h-8"
//               viewBox="0 0 780 500"
//               fill="none"
//               xmlns="http://www.w3.org/2000/svg"
//             >
//               <rect width="780" height="500" fill="white" />
//               <path
//                 d="M293.2 348.7H246.1L275.6 169.3H322.7L293.2 348.7Z"
//                 fill="#00579F"
//               />
//               <path
//                 d="M518.6 176.3C509.3 172.6 494.5 168.6 476.2 168.6C429.4 168.6 396.4 193.1 396.2 228C396 253.9 419.8 268.2 437.7 276.8C456.1 285.6 462.3 291.2 462.3 299.1C462.2 311.1 447.5 316.6 433.9 316.6C414.9 316.6 404.9 313.8 389.3 307L383.2 304.1L376.5 344.8C387.6 349.8 408.1 354.2 429.4 354.4C479.3 354.4 511.7 330.2 512.1 292.7C512.3 272.2 499.7 256.6 472.4 243.7C455.9 235.4 445.7 229.8 445.8 221.4C445.8 213.9 454.4 205.9 472.9 205.9C488.4 205.7 499.6 209.2 508.3 212.8L512.5 214.9L518.6 176.3Z"
//                 fill="#00579F"
//               />
//               <path
//                 d="M608.5 169.3H574.2C563.9 169.3 556.2 172.1 551.8 182.7L483.9 348.8H533.9C533.9 348.8 541.8 326.8 543.9 321.1C549.7 321.1 598.9 321.1 606.4 321.1C608 328.3 612.3 348.8 612.3 348.8H657.2L624.2 169.3H608.5ZM558.1 288.5C562.3 277.2 576.4 238.3 576.4 238.3C576.1 238.8 580.2 227.4 582.3 220.5L585.3 236.4C585.3 236.4 594.1 279.3 596.1 288.5H558.1Z"
//                 fill="#00579F"
//               />
//               <path
//                 d="M190.4 169.3L143.4 291.7L139.1 272.5C131.5 247.5 108.4 220.3 82.5 206.8L125.7 348.6H176L247.3 169.3H190.4Z"
//                 fill="#00579F"
//               />
//               <path
//                 d="M111.6 169.3H32.9L32 173.4C95.5 189.9 138.3 227.3 152.8 272.5L139.1 190.1C136.9 174.3 125.9 169.7 111.6 169.3Z"
//                 fill="#FAA61A"
//               />
//             </svg>

//             {/* Mastercard */}
//             <svg
//               className="h-8"
//               viewBox="0 0 780 500"
//               fill="none"
//               xmlns="http://www.w3.org/2000/svg"
//             >
//               <rect width="780" height="500" fill="white" />
//               <path d="M465.5 95.5h-151v260h151V95.5z" fill="#FF5F00" />
//               <path
//                 d="M324.3 225.5c0-53 24.9-100.2 63.7-130.5-28.3-22.3-64.1-35.6-103-35.6C193 59.4 118.4 134 118.4 225.5s74.6 166.7 166.7 166.7c38.9 0 74.7-13.3 103-35.6-38.8-30.3-63.7-77.5-63.7-130.5z"
//                 fill="#EB001B"
//               />
//               <path
//                 d="M657.8 225.5c0 92.1-74.6 166.7-166.7 166.7-38.9 0-74.7-13.3-103-35.6 38.8-30.3 63.7-77.5 63.7-130.5s-24.9-100.2-63.7-130.5c28.3-22.3 64.1-35.6 103-35.6 92.1 0 166.7 74.6 166.7 166.7z"
//                 fill="#F79E1B"
//               />
//             </svg>
//           </div>
//         </div>

//         {/* Display quote request message if applicable */}
//         {hasQuoteRequests && (
//           <Alert className="mb-4 bg-blue-50 border-blue-200">
//             <div className="flex items-start">
//               <AlertCircle
//                 className="h-5 w-5 mr-2 mt-0.5"
//                 style={{ color: config.colors.primary || "#3B82F6" }}
//               ></AlertCircle>
//               <div
//                 style={{
//                   color: config.colors.primary || "#3B82F6",
//                 }}
//               >
//                 <h4 className="font-medium ">Quote Request Included</h4>
//                 <AlertDescription>
//                   Your cart contains items marked as Quote Request. A sales
//                   representative will contact you about pricing for these items.
//                 </AlertDescription>
//               </div>
//             </div>
//           </Alert>
//         )}

//         <div className="space-y-3 mb-6">
//           {/* Coupon Section */}
//           <div className="mb-4">
//             <label
//               htmlFor="couponCode"
//               className="block text-sm font-medium text-gray-700"
//             >
//               Coupon Code
//             </label>
//             <div className="flex space-x-2 mt-1">
//               <input
//                 type="text"
//                 id="couponCode"
//                 value={couponCode}
//                 onChange={(e) => setCouponCode(e.target.value)}
//                 placeholder="Enter coupon code"
//                 className="flex-grow border rounded px-3 py-2"
//               />
//               {appliedCoupon ? (
//                 <button
//                   onClick={removeCoupon}
//                   className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
//                 >
//                   Remove
//                 </button>
//               ) : (
//                 <button
//                   onClick={validateCoupon}
//                   className="text-white px-4 py-2 rounded transition-colors"
//                   style={{
//                     backgroundColor: config.colors.primary || "#3B82F6",
//                     ":hover": {
//                       backgroundColor:
//                         `${config.colors.primary}DD` || "#3B82F6",
//                     },
//                   }}
//                 >
//                   Apply
//                 </button>
//               )}
//             </div>
//             {couponError && (
//               <Alert variant="destructive" className="mt-2">
//                 <AlertCircle className="h-4 w-4" />
//                 <AlertDescription>{couponError}</AlertDescription>
//               </Alert>
//             )}
//             {appliedCoupon && (
//               <div className="mt-2 text-sm text-green-600">
//                 Coupon "{appliedCoupon.code}" applied successfully
//               </div>
//             )}
//           </div>

//           {/* Order Summary Details */}
//           <div className="flex justify-between text-gray-600">
//             <span>Subtotal</span>
//             <span>${subtotal.toFixed(2)}</span>
//           </div>

//           {/* Coupon Discount */}
//           {discountAmount > 0 && (
//             <div className="flex justify-between text-green-600">
//               <span>Coupon Discount</span>
//               <span>-${discountAmount.toFixed(2)}</span>
//             </div>
//           )}

//           {gstTotal > 0 && (
//             <div className="flex justify-between text-gray-600">
//               <span>GST (5%)</span>
//               <span>${gstTotal.toFixed(2)}</span>
//             </div>
//           )}
//           {pstTotal > 0 && (
//             <div className="flex justify-between text-gray-600">
//               <span>PST (7%)</span>
//               <span>${pstTotal.toFixed(2)}</span>
//             </div>
//           )}
//           <div className="h-px bg-gray-200 my-2"></div>
//           <div className="flex justify-between text-lg font-bold text-gray-900">
//             <span>Total</span>
//             <span>${total.toFixed(2)}</span>
//           </div>
//         </div>

//         {/* Updated Stripe Elements rendering with proper loading and error states */}
//         {isStripeLoading ? (
//           <div className="flex justify-center items-center p-8">
//             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//             <span className="ml-2">Loading payment system...</span>
//           </div>
//         ) : stripeError ? (
//           <div className="p-4 text-red-500 bg-red-50 rounded border border-red-200">
//             {stripeError}
//           </div>
//         ) : stripePromise ? (
//           <Elements
//             stripe={stripePromise}
//             options={{
//               locale: "en",
//               appearance: {
//                 theme: "stripe",
//               },
//             }}
//           >
//             <Suspense fallback={null}>
//               <CheckoutForm
//                 selectedItems={selectedItems}
//                 quantities={quantities}
//                 total={total}
//                 coupon={appliedCoupon}
//                 hasQuoteRequests={hasQuoteRequests}
//                 selectedAddress={selectedAddress}
//                 deliveryInstructions={deliveryInstructions}
//                 selectedDeliveryMethod={selectedDeliveryMethod}
//                 deliveryCost={
//                   selectedDeliveryMethod === "delivery" ? deliveryCost : 0
//                 }
//                 taxInfo={{
//                   subtotal: subtotal,
//                   discountedSubtotal: discountedSubtotal,
//                   gstTotal: gstTotal,
//                   pstTotal: pstTotal,
//                   deliveryCost:
//                     selectedDeliveryMethod === "delivery" ? deliveryCost : 0,
//                 }}
//               />
//             </Suspense>
//           </Elements>
//         ) : (
//           <div className="p-4 text-red-500 bg-red-50 rounded border border-red-200">
//             Unable to load payment system. Please refresh the page or try again
//             later.
//           </div>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gray-50 ">
//       <h2 className="text-3xl font-bold text-gray-900 mb-2 border-b pb-4">
//         Shopping Cart
//       </h2>

//       <CartSignInUp
//         isOpen={showSignInModal}
//         onClose={() => setShowSignInModal(false)}
//       />

//       {cart.length === 0 ? (
//         <p className="text-gray-600 rounded">
//           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 320">
//             {/* Background with lighter gradient */}
//             <defs>
//               <linearGradient
//                 id="backgroundGradient"
//                 x1="0%"
//                 y1="0%"
//                 x2="0%"
//                 y2="100%"
//               >
//                 <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
//                 <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
//               </linearGradient>
//               <filter
//                 id="lightShadow"
//                 x="-20%"
//                 y="-20%"
//                 width="140%"
//                 height="140%"
//               >
//                 <feDropShadow
//                   dx="0"
//                   dy="2"
//                   stdDeviation="1.5"
//                   floodOpacity="0.07"
//                 />
//               </filter>
//             </defs>

//             {/* Main background - transparent to make it lighter */}
//             <rect
//               width="500"
//               height="320"
//               fill="url(#backgroundGradient)"
//               rx="0"
//               ry="0"
//               opacity="0.8"
//             />

//             {/* Shopping Cart Icon - lighter style - centered and smaller */}
//             <g
//               transform="translate(250, 120) scale(6)"
//               filter="url(#lightShadow)"
//             >
//               {/* This is based on W3C's icon style but with lighter stroke */}
//               <path
//                 d="M-7,8 L-9,2 L5,2 L3,8 Z"
//                 fill="white"
//                 stroke="#9ca3af"
//                 strokeWidth="0.45"
//               />
//               <line
//                 x1="-9"
//                 y1="2"
//                 x2="-11"
//                 y2="-3"
//                 stroke="#9ca3af"
//                 strokeWidth="0.45"
//               />
//               <line
//                 x1="-11"
//                 y1="-3"
//                 x2="-13"
//                 y2="-3"
//                 stroke="#9ca3af"
//                 strokeWidth="0.45"
//               />

//               {/* Cart wheels - lighter */}
//               <circle
//                 cx="-5"
//                 cy="10"
//                 r="1.5"
//                 fill="white"
//                 stroke="#9ca3af"
//                 strokeWidth="0.35"
//               />
//               <circle
//                 cx="1"
//                 cy="10"
//                 r="1.5"
//                 fill="white"
//                 stroke="#9ca3af"
//                 strokeWidth="0.35"
//               />
//             </g>

//             {/* Text Element - lighter color */}
//             <text
//               x="250"
//               y="240"
//               fontFamily="Arial, sans-serif"
//               fontSize="18"
//               fontWeight="400"
//               fill="#9ca3af"
//               textAnchor="middle"
//             >
//               Your cart is empty
//             </text>
//           </svg>
//         </p>
//       ) : (
//         <>
//           <div className="mb-1 flex items-center">
//             <button
//               onClick={toggleSelectAll}
//               style={{
//                 boxShadow: "none",
//                 border: "none",
//                 background: "transparent",
//                 transform: "none",
//               }}
//             >
//               {selectedItems.size === cart.length
//                 ? "Deselect All Items"
//                 : "Select All Items"}
//             </button>
//           </div>

//           <div className="space-y-4 mb-8">
//             {cart.map((item, index) => (
//               <div
//                 key={index}
//                 className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-4 transition-all hover:shadow-md relative"
//               >
//                 <div className="flex flex-col sm:flex-row">
//                   {/* Checkbox and Image column */}
//                   <div className="p-4 w-48  flex sm:flex-col items-start relative">
//                     <Checkbox
//                       checked={selectedItems.has(index)}
//                       onCheckedChange={() => toggleItem(index)}
//                       className="mt-1 mr-4 sm:mr-0 sm:mb-4 h-5 w-5"
//                     />
//                     {/* Mobile remove button - visible ONLY on small screens */}
//                     <button
//                       onClick={() => {
//                         // Try to play remove sound
//                         try {
//                           const audio = new Audio(removeSound);
//                           audio
//                             .play()
//                             .catch((e) =>
//                               console.log("Audio failed to play:", e)
//                             );
//                         } catch (e) {
//                           console.log("Audio error:", e);
//                         }
//                         removeFromCart(index);
//                       }}
//                       className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm z-10 text-red-500 sm:hidden"
//                       aria-label="Remove item"
//                       style={{
//                         boxShadow: "none",
//                         border: "none",
//                         background: "transparent",
//                         transform: "none",
//                         marginLeft: "auto",
//                         marginRight: -120 + "px",
//                       }}
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-5 w-5"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
//                         />
//                       </svg>
//                     </button>

//                     {/* Larger image */}
//                     <div className="w-full sm:w-full h-40 flex items-center justify-center flex-shrink-0">
//                       <img
//                         src={
//                           // item.customization?.preview ||
//                           // item.product?.images?.[0]?.data ||
//                           // item.customization?.customFields?.find(
//                           //   (field) => field.type === "image"
//                           // )?.content
//                           // First priority: preview URL from processed customization
//                           item.customization?.previewUrl ||
//                           // Second priority: preview path with API prefix
//                           (item.customization?.preview &&
//                             item.customization.preview.startsWith("/")
//                             ? `/api${item.customization.preview}`
//                             : item.customization.preview) ||
//                           // Third priority: product images with new file structure
//                           (item.product?.images?.[0]?.path
//                             ? `/api${item.product.images[0].path}`
//                             : item.product?.images?.[0]?.data) ||
//                           // Fourth priority: custom field images with URL handling
//                           (() => {
//                             const imageField =
//                               item.customization?.customFields?.find(
//                                 (field) =>
//                                   (field.type === "image" ||
//                                     field.type === "logo") &&
//                                   (field.imageUrl || field.content)
//                               );
//                             if (imageField) {
//                               if (
//                                 imageField.imageUrl &&
//                                 imageField.imageUrl.startsWith("/")
//                               ) {
//                                 return `/api${imageField.imageUrl}`;
//                               }
//                               return imageField.content;
//                             }
//                             return null;
//                           })() ||
//                           // Final fallback
//                           "https://via.placeholder.com/150?text=No+Image"
//                         }
//                         alt={item.product?.name}
//                         className="w-full h-full object-cover rounded-md"
//                         onError={(e) => {
//                           e.target.src =
//                             "https://via.placeholder.com/150?text=No+Image";
//                         }}
//                       />
//                     </div>
//                   </div>

//                   {/* Product details column */}
//                   <div className="flex-1 p-4 border-t sm:border-t-0 sm:border-l border-gray-100">
//                     <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
//                       <div className="flex-1">
//                         <h3 className="text-lg font-bold text-gray-900 mb-2">
//                           {item.product?.name}
//                         </h3>

//                         {/* Quantity selector */}
//                         <div className="mt-4 flex items-center">
//                           <label
//                             htmlFor={`quantity-${index}`}
//                             className="text-sm font-medium text-gray-700 mr-3"
//                           >
//                             Quantity:
//                           </label>

//                           {onlyPricingTier ? (
//                             // When onlyPricingTier is true, use different UI based on whether item has pricing tiers
//                             item.product.pricingTiers &&
//                               item.product.pricingTiers.length > 0 ? (
//                               // Use dropdown for items with pricing tiers when onlyPricingTier is true
//                               <select
//                                 id={`quantity-${index}`}
//                                 value={quantities[index]}
//                                 onChange={(e) =>
//                                   updateQuantity(
//                                     index,
//                                     parseInt(e.target.value)
//                                   )
//                                 }
//                                 className={selectClassName}
//                                 style={{
//                                   borderColor: config.colors.textSecondary,
//                                   boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
//                                   ":focus": {
//                                     borderColor: config.colors.primary,
//                                     boxShadow: `0 0 0 2px ${config.colors.primary}30`,
//                                     outline: "none",
//                                   },
//                                 }}
//                               >
//                                 {generateTierOptions(item, index)}
//                               </select>
//                             ) : (
//                               // Use +/- increment for items without pricing tiers when onlyPricingTier is true
//                               <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
//                                 <button
//                                   onClick={() =>
//                                     updateQuantity(index, quantities[index] - 1)
//                                   }
//                                   className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
//                                   disabled={
//                                     quantities[index] <=
//                                     (item.product?.minimumOrder || 1)
//                                   }
//                                 >
//                                   −
//                                 </button>
//                                 <input
//                                   type="number"
//                                   value={quantities[index]}
//                                   onChange={(e) =>
//                                     updateQuantity(
//                                       index,
//                                       parseInt(e.target.value)
//                                     )
//                                   }
//                                   className="w-16 text-center border-x border-gray-300 py-1 focus:outline-none"
//                                   min={item.product?.minimumOrder || 1}
//                                 />
//                                 <button
//                                   onClick={() =>
//                                     updateQuantity(index, quantities[index] + 1)
//                                   }
//                                   className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
//                                 >
//                                   +
//                                 </button>
//                               </div>
//                             )
//                           ) : (
//                             // When onlyPricingTier is false, always use +/- increment regardless of pricing tiers
//                             <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
//                               <button
//                                 onClick={() =>
//                                   updateQuantity(index, quantities[index] - 1)
//                                 }
//                                 className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
//                                 disabled={
//                                   quantities[index] <=
//                                   (item.product?.minimumOrder || 1)
//                                 }
//                               >
//                                 −
//                               </button>
//                               <input
//                                 type="number"
//                                 value={quantities[index]}
//                                 onChange={(e) =>
//                                   updateQuantity(
//                                     index,
//                                     parseInt(e.target.value)
//                                   )
//                                 }
//                                 className="w-24 text-center border-x border-gray-300 py-1 focus:outline-none"
//                                 min={item.product?.minimumOrder || 1}
//                               />
//                               <button
//                                 onClick={() =>
//                                   updateQuantity(index, quantities[index] + 1)
//                                 }
//                                 className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 focus:outline-none"
//                               >
//                                 +
//                               </button>
//                             </div>
//                           )}

//                           {/* Add Quote Request button */}
//                           {/* {!onlyPricingTier && (
//                         <button
//                           onClick={() => updateQuantity(index, 20000)}
//                           className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 focus:outline-none"
//                         >
//                           Request Quote
//                         </button>
//                       )} */}
//                         </div>

//                         {/* Order Description - if exists */}
//                         {item.customization?.description && (
//                           <div className="text-sm text-gray-600 mb-3 max-w-md mt-6">
//                             <span className="font-medium text-gray-700">
//                               Description:{" "}
//                             </span>
//                             {item.customization.description}
//                           </div>
//                         )}

//                         {/* Customization Details Toggle */}
//                         {(item.customization?.template ||
//                           (item.customization?.requiredFields &&
//                             item.customization?.requiredFields.length > 0) ||
//                           (item.customization?.customFields &&
//                             item.customization?.customFields.length > 0)) && (
//                             <button
//                               onClick={() => toggleDetails(index)}
//                               style={{
//                                 boxShadow: "none",
//                                 border: "none",
//                                 background: "transparent",
//                                 transform: "none",
//                                 color: config.colors.primary,
//                               }}
//                               className=" text-sm font-medium flex items-center mt-4"
//                             >
//                               {expandedDetails.has(index) ? (
//                                 <>
//                                   <svg
//                                     xmlns="http://www.w3.org/2000/svg"
//                                     className="h-4 w-4 mr-1"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                     stroke="currentColor"
//                                   >
//                                     <path
//                                       strokeLinecap="round"
//                                       strokeLinejoin="round"
//                                       strokeWidth={2}
//                                       d="M19 9l-7 7-7-7"
//                                     />
//                                   </svg>
//                                   Hide Details
//                                 </>
//                               ) : (
//                                 <>
//                                   <svg
//                                     xmlns="http://www.w3.org/2000/svg"
//                                     className="h-4 w-4 mr-1"
//                                     fill="none"
//                                     viewBox="0 0 24 24"
//                                     stroke="currentColor"
//                                   >
//                                     <path
//                                       strokeLinecap="round"
//                                       strokeLinejoin="round"
//                                       strokeWidth={2}
//                                       d="M9 5l7 7-7 7"
//                                     />
//                                   </svg>
//                                   Show Details
//                                 </>
//                               )}
//                             </button>
//                           )}

//                         {/* Expanded Customization Details */}
//                         {expandedDetails.has(index) && (
//                           <div className="mt-4 space-y-3 p-3 bg-gray-50 rounded-md max-w-md">
//                             {item.customization?.template && (
//                               <div className="text-sm">
//                                 <span className="font-medium text-gray-700">
//                                   Template:{" "}
//                                 </span>
//                                 <span className="text-gray-600">
//                                   {item.customization.template.name}
//                                 </span>
//                               </div>
//                             )}

//                             {/* Required Fields */}
//                             {item.customization?.requiredFields?.length > 0 && (
//                               <div>
//                                 <h4 className="text-sm font-medium text-gray-700 mb-1">
//                                   Required Fields:
//                                 </h4>
//                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
//                                   {item.customization.requiredFields.map(
//                                     (field, fieldIndex) => (
//                                       <div key={fieldIndex} className="text-sm">
//                                         <p className="font-medium text-gray-700">
//                                           {field.type}:
//                                         </p>
//                                         {field.type === "text" ? (
//                                           <p className="text-gray-600">
//                                             {field.value}
//                                           </p>
//                                         ) : (
//                                           <img
//                                             src={field.value}
//                                             alt={`${field.type}`}
//                                             className="w-16 h-16 object-contain border rounded"
//                                           />
//                                         )}
//                                       </div>
//                                     )
//                                   )}
//                                 </div>
//                               </div>
//                             )}

//                             {/* Custom Fields */}
//                             {item.customization?.customFields?.length > 0 && (
//                               <div>
//                                 <h4 className="text-sm font-medium text-gray-700 mb-1">
//                                   Custom Fields:
//                                 </h4>
//                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
//                                   {item.customization.customFields.map(
//                                     (field, fieldIndex) => (
//                                       <div key={fieldIndex} className="text-sm">
//                                         <p className="font-medium text-gray-700">
//                                           {field.type}:
//                                         </p>
//                                         {field.type === "text" ? (
//                                           <p className="text-gray-600">
//                                             {field.content}
//                                           </p>
//                                         ) : (
//                                           // <img
//                                           //   src={field.content}
//                                           //   alt="Custom upload"
//                                           //   className="w-106 h-106 object-contain border rounded"
//                                           // />
//                                           <p></p>
//                                         )}
//                                       </div>
//                                     )
//                                   )}
//                                 </div>
//                               </div>
//                             )}
//                           </div>
//                         )}
//                       </div>

//                       <div className="mt-4 sm:mt-0 sm:ml-6 sm:text-right">
//                         {/* Desktop remove button - ONLY visible on larger screens */}
//                         <button
//                           onClick={() => {
//                             // Try to play remove sound
//                             try {
//                               const audio = new Audio(removeSound);
//                               audio
//                                 .play()
//                                 .catch((e) =>
//                                   console.log("Audio failed to play:", e)
//                                 );
//                             } catch (e) {
//                               console.log("Audio error:", e);
//                             }
//                             removeFromCart(index);
//                           }}
//                           style={{
//                             boxShadow: "none",
//                             border: "none",
//                             background: "transparent",
//                             transform: "none",
//                             marginLeft: 100 + "px",
//                           }}
//                           className="text-red-500 hover:text-red-700 hidden sm:flex items-right text-sm font-medium mb-2"
//                         >
//                           <svg
//                             xmlns="http://www.w3.org/2000/svg"
//                             className="h-5 w-5 mr-1"
//                             fill="none"
//                             viewBox="0 0 24 24"
//                             stroke="currentColor"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth={2}
//                               d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
//                             />
//                           </svg>
//                           {/* Remove */}
//                         </button>

//                         {/* Price information */}
//                         {quantities[index] < 20000 ? (
//                           <div className="mt-2">
//                             {typeof calculateItemPrice(item, index) ===
//                               "string" ? (
//                               // Legacy string format handling
//                               <div className="text-xl font-semibold text-gray-900">
//                                 ${calculateItemPrice(item, index)}
//                               </div>
//                             ) : (
//                               // New detailed price object format
//                               <div>
//                                 <div className="text-xl font-semibold text-gray-900">
//                                   ${calculateItemPrice(item, index).total}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {/* Show unit price and quantity */}
//                                   <div>
//                                     {calculateItemPrice(item, index).quantity} ×
//                                     ${calculateItemPrice(item, index).unitPrice}
//                                     /each
//                                   </div>

//                                   {/* Show pricing information based on scenario */}
//                                   {/* {!onlyPricingTier && (
//                                   <div>
//                                     {calculateItemPrice(item, index).tier ? (
//                                       // Show tier information
//                                       <div style={{ color: config.colors.secondary }}>
//                                         {calculateItemPrice(item, index).tier.maxQuantity
//                                           ? `Tier: ${calculateItemPrice(item, index).tier.minQuantity}-${calculateItemPrice(item, index).tier.maxQuantity}`
//                                           : `Tier: ${calculateItemPrice(item, index).tier.minQuantity}+`}
//                                       </div>
//                                     ) : calculateItemPrice(item, index).isBusinessOrder ? (
//                                       // Show business order indication
//                                       <div style={{ color: config.colors.primary, fontWeight: 500 }}>Business Pricing</div>
//                                     ) : (
//                                       // Show regular price indication
//                                       <div>Regular Price</div>
//                                     )}
//                                   </div>
//                                 )} */}
//                                   {!onlyPricingTier && (
//                                     <div>
//                                       {calculateItemPrice(item, index)
//                                         .isWholesale ? (
//                                         // Show wholesale pricing indication
//                                         <div
//                                           style={{
//                                             color: config.colors.primary,
//                                             fontWeight: 500,
//                                           }}
//                                         >
//                                           Wholesale Pricing
//                                         </div>
//                                       ) : calculateItemPrice(item, index)
//                                         .tier ? (
//                                         // Show tier information
//                                         <div
//                                           style={{
//                                             color: config.colors.secondary,
//                                           }}
//                                         >
//                                           {calculateItemPrice(item, index).tier
//                                             .maxQuantity
//                                             ? `Tier: ${calculateItemPrice(item, index)
//                                               .tier.minQuantity
//                                             }-${calculateItemPrice(item, index)
//                                               .tier.maxQuantity
//                                             }`
//                                             : `Tier: ${calculateItemPrice(item, index)
//                                               .tier.minQuantity
//                                             }+`}
//                                         </div>
//                                       ) : calculateItemPrice(item, index)
//                                         .isBusinessOrder ? (
//                                         // Show business order indication
//                                         <div
//                                           style={{
//                                             color: config.colors.primary,
//                                             fontWeight: 500,
//                                           }}
//                                         >
//                                           Business Pricing
//                                         </div>
//                                       ) : (
//                                         // Show regular price indication
//                                         <div>Regular Price</div>
//                                       )}
//                                     </div>
//                                   )}

//                                   {/* Tax information */}
//                                   {item.product?.hasGST && (
//                                     <div>+ GST (5%)</div>
//                                   )}
//                                   {item.product?.hasPST && (
//                                     <div>+ PST (7%)</div>
//                                   )}
//                                 </div>
//                               </div>
//                             )}
//                           </div>
//                         ) : (
//                           <div
//                             className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
//                             style={{
//                               backgroundColor: `${config.colors.primary}20`,
//                               color: config.colors.primary,
//                             }}
//                           >
//                             Quote Request
//                           </div>
//                         )}

//                         {!item.product?.inStock && (
//                           <div className="mt-2 text-red-600 font-medium text-sm">
//                             Out of stock
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* {selectedItems.size > 0 && user && renderAddressSection()} */}
//           {selectedItems.size > 0 && user && (
//             <CartShippingAddress
//               user={user}
//               selectedAddress={selectedAddress}
//               setSelectedAddress={setSelectedAddress}
//               deliveryInstructions={deliveryInstructions}
//               setDeliveryInstructions={setDeliveryInstructions}
//               refreshUser={refreshUser}
//             />
//           )}

//           {selectedItems.size > 0 && user &&
//             // isRegularPriceOnly &&
//             (
//               <DeliveryOptions
//                 selectedDeliveryMethod={selectedDeliveryMethod}
//                 setSelectedDeliveryMethod={setSelectedDeliveryMethod}
//                 deliveryCost={deliveryCost}
//                 setDeliveryCost={setDeliveryCost}
//                 userLocation={user?.addresses?.find(
//                   (addr) =>
//                     addr._id?.toString() === selectedAddress ||
//                     `index-${user.addresses.indexOf(addr)}` === selectedAddress
//                 )}
//                 estimatedDeliveryTime={estimatedDeliveryTime}
//                 setEstimatedDeliveryTime={setEstimatedDeliveryTime}
//               />
//             )}

//           {selectedItems.size > 0 && (
//             <div className="bg-white rounded-lg shadow-md p-6">
//               <div className="mb-6">
//                 <h3 className="text-xl font-bold mb-4 text-gray-900">
//                   Order Summary
//                 </h3>
//                 <div className="h-px bg-gray-200 my-4"></div>
//                 {renderPaymentMethod()}

//                 {/* Delivery Fee if applicable */}
//                 {selectedDeliveryMethod === "delivery" && deliveryCost > 0 && (
//                   <div className="flex justify-between text-gray-600">
//                     <span>Delivery Fee</span>
//                     <span>${deliveryCost.toFixed(2)}</span>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </>
//       )}
//     </div>
//   );
// };

// export default Cart;

